import axios, { AxiosRequestConfig } from 'axios';
import { supabase } from 'src/lib/supabase';

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Serialize arrays in repeated parameter format (e.g., exclude=value1&exclude=value2)
  paramsSerializer: params => {
    const searchParams = new URLSearchParams();
    for (const key in params) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    }
    return searchParams.toString();
  },
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle unauthorized responses
      if (status === 401) {
        const authError = new Error('Unauthorized: Please log in again');
        return Promise.reject(authError);
      }

      // Handle rate limiting responses
      if (status === 429) {
        const resetTime = data.resetTime ? new Date(data.resetTime) : null;
        const retryAfter = resetTime ? Math.ceil((resetTime.getTime() - Date.now()) / 1000 / 60) : null;
        
        let message = 'Rate limit exceeded. ';
        if (retryAfter && retryAfter > 0) {
          message += `Please try again in ${retryAfter} minute${retryAfter !== 1 ? 's' : ''}.`;
        } else {
          message += 'Please try again later.';
        }
        
        const rateLimitError = new Error(message) as Error & { 
          isRateLimit: boolean; 
          rateLimitInfo?: Record<string, unknown>; 
          retryAfter?: number;
        };
        rateLimitError.isRateLimit = true;
        rateLimitError.rateLimitInfo = data;
        rateLimitError.retryAfter = retryAfter || undefined;
        
        return Promise.reject(rateLimitError);
      }

      // Handle other HTTP errors
      const message = data?.error || 'An error occurred';
      return Promise.reject(new Error(message));
    } else if (error.request) {
      // The request was made but no response was received
      return Promise.reject(new Error('No response from server'));
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(error);
    }
  }
);

// Create a custom type for the axios instance
interface CustomAxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
}

// Cast the instance to our custom type
const customAxiosInstance = axiosInstance as unknown as CustomAxiosInstance;

export default customAxiosInstance; 